<script setup lang="ts">
import { computed } from "vue";
import { appScopes, sensitiveAppScopes, type AppScope } from "@artech-global/types";

const props = defineProps<{
  modelValue: AppScope[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: AppScope[]): void;
}>();

const { t } = useI18n();

// One row per resource, read/write inline — same shape as the admin role
// permission picker so both forms read the same way.
const groups = computed(() => {
  const order: string[] = [];
  const byResource: Record<string, AppScope[]> = {};

  for (const scope of appScopes) {
    const resource = scope.replace(/^(read|write)_/, "");
    if (!byResource[resource]) {
      byResource[resource] = [];
      order.push(resource);
    }
    byResource[resource]!.push(scope);
  }

  return order.map((resource) => ({
    key: resource,
    label: t(`app.scopeGroup.${resource}`),
    scopes: byResource[resource]!.map((value) => ({
      value,
      label: t(`app.scopeAction.${value.startsWith("write_") ? "write" : "read"}`),
      isSensitive: sensitiveAppScopes.includes(value),
      // Write implies read: the read box stays ticked and locked.
      isForced: !value.startsWith("write_") && props.modelValue.includes(`write_${resource}` as AppScope),
    })),
  }));
});

const selectedCount = computed(() => appScopes.filter((s) => props.modelValue.includes(s)).length);

const allState = computed((): boolean | "indeterminate" => {
  if (selectedCount.value === 0) return false;
  if (selectedCount.value === appScopes.length) return true;
  return "indeterminate";
});

function has(scope: AppScope) {
  return props.modelValue.includes(scope);
}

function toggle(scope: AppScope) {
  const resource = scope.replace(/^(read|write)_/, "");
  let scopes = [...props.modelValue];

  if (scopes.includes(scope)) {
    scopes = scopes.filter((s) => s !== scope);
    // Dropping read drops the whole resource; write alone means nothing.
    if (!scope.startsWith("write_")) scopes = scopes.filter((s) => !s.endsWith(`_${resource}`));
  } else {
    scopes.push(scope);
    if (scope.startsWith("write_")) scopes.push(`read_${resource}` as AppScope);
  }

  emit("update:modelValue", [...new Set(scopes)]);
}

function toggleAll() {
  emit("update:modelValue", selectedCount.value === appScopes.length ? [] : [...appScopes]);
}
</script>

<template>
  <div class="scope-picker">
    <div class="scope-picker__row scope-picker__toolbar">
      <FormCheckbox
        :model-value="allState"
        :disabled="disabled"
        :aria-label="t('app.scopesTitle')"
        @update:model-value="toggleAll"
      >
        <template #label>
          <span class="text-body-secondary">
            {{ t("app.scopesSelected", { selected: selectedCount, total: appScopes.length }) }}
          </span>
        </template>
      </FormCheckbox>
    </div>

    <div v-for="group in groups" :key="group.key" class="scope-picker__row">
      <span class="text-body-secondary scope-picker__resource">{{ group.label }}</span>

      <div class="scope-picker__actions">
        <div v-for="scope in group.scopes" :key="scope.value" class="flex items-center gap-2xsmall">
          <FormCheckbox
            :model-value="has(scope.value)"
            :label="scope.label"
            :disabled="disabled || scope.isForced"
            @update:model-value="toggle(scope.value)"
          />
          <Badge v-if="scope.isSensitive" intent="warning" emphasis="medium" size="small">
            {{ t("app.scopeSensitive") }}
          </Badge>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$divider-light: token("color-border-neutral-alpha-light-default");

.scope-picker {
  border: 1px solid $divider-light;
  border-radius: token("corner-radius-xsmall");

  &__row {
    display: flex;
    align-items: center;
    gap: token("gap-medium");
    padding: token("padding-small") token("padding-medium");
    border-bottom: 1px solid $divider-light;

    &:last-child {
      border-bottom: none;
    }
  }

  &__toolbar {
    background: token("color-bg-surface-alt");
  }

  &__resource {
    width: 140px;
    flex-shrink: 0;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: token("gap-medium");
  }

  @include down("md") {
    &__row {
      align-items: flex-start;
      flex-direction: column;
      gap: token("gap-small");
    }

    &__resource {
      width: auto;
    }
  }
}
</style>
